import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Card,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  TextField,
  Button,
  Alert,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText as MuiListItemText,
  Checkbox,
  MenuItem,
} from "@mui/material";

import ChecklistIcon from "@mui/icons-material/Checklist";
import GroupsIcon from "@mui/icons-material/Groups";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";

import configServ from "../services/config";

import {
  rSetShowFab,
} from "../redux/reducers/GlobalVariables";

// ------------------------------------------------------------------
// Constants
// ------------------------------------------------------------------

const DAYS = [
  {
    key: "mon",
    label: "Monday",
    short: "Mon",
  },
  {
    key: "tue",
    label: "Tuesday",
    short: "Tue",
  },
  {
    key: "wed",
    label: "Wednesday",
    short: "Wed",
  },
  {
    key: "thu",
    label: "Thursday",
    short: "Thu",
  },
  {
    key: "fri",
    label: "Friday",
    short: "Fri",
  },
  {
    key: "sat",
    label: "Saturday",
    short: "Sat",
  },
  {
    key: "sun",
    label: "Sunday",
    short: "Sun",
  },
];

const DAY_NUMBER_TO_KEY = {
  0: "sun",
  1: "mon",
  2: "tue",
  3: "wed",
  4: "thu",
  5: "fri",
  6: "sat",
};

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function pad(n) {
  return String(n).padStart(2, "0");
}

function toInputDate(value) {
  if (!value) {
    return "";
  }

  if (value instanceof Date) {
    return `${value.getFullYear()}-${pad(
      value.getMonth() + 1
    )}-${pad(value.getDate())}`;
  }

  return String(value).substring(0, 10);
}

function fmtDMY(date) {
  return `${pad(date.getDate())}/${pad(
    date.getMonth() + 1
  )}/${date.getFullYear()}`;
}

function addMonths(date, months) {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
}

function getFYBounds(today) {
  const year = today.getFullYear();
  const month = today.getMonth();

  /*
   * April = 3
   */
  const fyStartYear =
    month >= 3
      ? year
      : year - 1;

  return {
    fyStart: new Date(
      fyStartYear,
      3,
      1
    ),

    fyEnd: new Date(
      fyStartYear + 1,
      2,
      31
    ),
  };
}

// ------------------------------------------------------------------
// Employee Selector Modal
// ------------------------------------------------------------------

function EmployeeSelectorModal({
  open,
  onClose,
  selectedEmployees,
  onSave,
  companyId,
  userId,
  isAdmin,
}) {
  const [search, setSearch] =
    useState("");

  const [debouncedSearch, setDebouncedSearch] =
    useState("");

  const [employeeList, setEmployeeList] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [page, setPage] =
    useState(1);

  const [hasMore, setHasMore] =
    useState(true);

  const [total, setTotal] =
    useState(0);

  const [selectedIds, setSelectedIds] =
    useState([]);

  const perPage = 20;

  // --------------------------------------------------------------
  // Open modal
  // --------------------------------------------------------------

  useEffect(() => {
    if (!open) {
      return;
    }

    setSelectedIds(
      selectedEmployees.map(
        (employee) =>
          Number(employee.id)
      )
    );

    setSearch("");
    setDebouncedSearch("");
    setEmployeeList([]);
    setPage(1);
    setHasMore(true);

    fetchEmployees(1, "");

    // eslint-disable-next-line
  }, [open]);

  // --------------------------------------------------------------
  // Search debounce
  // --------------------------------------------------------------

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
      setEmployeeList([]);
      setHasMore(true);
    }, 500);

    return () => {
      clearTimeout(timer);
    };
  }, [search]);

  // --------------------------------------------------------------
  // Search fetch
  // --------------------------------------------------------------

  useEffect(() => {
    if (!open) {
      return;
    }

    fetchEmployees(
      1,
      debouncedSearch
    );

    // eslint-disable-next-line
  }, [debouncedSearch]);

  // --------------------------------------------------------------
  // Fetch employees
  // --------------------------------------------------------------

  const fetchEmployees = async (
    pageNum,
    searchTerm
  ) => {
    if (!companyId || !userId) {
      return;
    }

    setLoading(true);

    try {
      const payload = {
        company_id: companyId,
        user_id: userId,
        is_admin: isAdmin,
        status: 1,
        per_page: perPage,
        page: pageNum,
      };

      if (searchTerm?.trim()) {
        payload.search =
          searchTerm.trim();
      }

      const response =
        await configServ.getEmployees(
          payload
        );

      if (
        response?.status === true
      ) {
        const {
          data = [],
          pagination = {},
        } = response;

        const mapped =
          data.map(
            (employee) => ({
              id: Number(
                employee.id
              ),

              code:
                employee.employee_code ||
                employee.emp_code ||
                "",

              name:
                employee.contact_name ||
                `${employee.fname || ""} ${employee.lname || ""
                  }`.trim() ||
                employee.name ||
                "Unnamed",
            })
          );

        setEmployeeList(
          (previous) =>
            pageNum === 1
              ? mapped
              : [
                ...previous,
                ...mapped,
              ]
        );

        setTotal(
          pagination.total || 0
        );

        setHasMore(
          pageNum <
          (pagination.last_page || 1)
        );
      } else {
        throw new Error(
          response?.message ||
          "Failed to fetch employees"
        );
      }
    } catch (error) {
      // toast removed
    } finally {
      setLoading(false);
    }
  };

  // --------------------------------------------------------------
  // Infinite scroll
  // --------------------------------------------------------------

  useEffect(() => {
    if (
      !open ||
      loading ||
      !hasMore
    ) {
      return;
    }

    const observer =
      new IntersectionObserver(
        (entries) => {
          if (
            entries[0].isIntersecting
          ) {
            const nextPage =
              page + 1;

            setPage(nextPage);

            fetchEmployees(
              nextPage,
              debouncedSearch
            );
          }
        },
        {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        }
      );

    const sentinel =
      document.getElementById(
        "employee-sentinel"
      );

    if (sentinel) {
      observer.observe(
        sentinel
      );
    }

    return () => {
      observer.disconnect();
    };

    // eslint-disable-next-line
  }, [
    open,
    loading,
    hasMore,
    page,
    debouncedSearch,
  ]);

  // --------------------------------------------------------------
  // Toggle employee
  // --------------------------------------------------------------

  const toggleEmployee = (id) => {
    setSelectedIds(
      (previous) =>
        previous.includes(id)
          ? previous.filter(
            (item) =>
              item !== id
          )
          : [
            ...previous,
            id,
          ]
    );
  };

  // --------------------------------------------------------------
  // Save employees
  // --------------------------------------------------------------

  const handleSave = () => {
    const selectedObjects =
      selectedIds.map(
        (id) => {
          const existing =
            employeeList.find(
              (employee) =>
                Number(
                  employee.id
                ) ===
                Number(id)
            );

          return (
            existing || {
              id: Number(id),
              code: "",
              name: `Employee #${id}`,
            }
          );
        }
      );

    onSave(selectedObjects);
    onClose();
  };

  // --------------------------------------------------------------
  // UI
  // --------------------------------------------------------------

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          pb: 1,
        }}
      >
        Select Employees

        <Typography
          variant="caption"
          sx={{
            display: "block",
            color:
              "text.secondary",
            fontWeight: 400,
          }}
        >
          {total} total active
          employees
        </Typography>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 2 }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, code"
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          InputProps={{
            startAdornment: (
              <SearchIcon
                sx={{
                  color:
                    "text.secondary",
                  mr: 1,
                }}
              />
            ),
          }}
          sx={{ mb: 2 }}
        />

        <List
          dense
          sx={{
            maxHeight: 400,
            overflowY: "auto",
          }}
        >
          {employeeList.map(
            (employee) => (
              <ListItem
                key={employee.id}
                button
                onClick={() =>
                  toggleEmployee(
                    Number(
                      employee.id
                    )
                  )
                }
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selectedIds.includes(
                      Number(
                        employee.id
                      )
                    )}
                    disableRipple
                  />
                </ListItemIcon>

                <MuiListItemText
                  primary={
                    employee.name
                  }
                  secondary={
                    employee.code
                  }
                />
              </ListItem>
            )
          )}

          {loading && (
            <ListItem>
              <CircularProgress
                size={24}
                sx={{
                  mx: "auto",
                }}
              />
            </ListItem>
          )}

          <div
            id="employee-sentinel"
            style={{
              height: 1,
            }}
          />

          {!loading &&
            employeeList.length ===
            0 &&
            debouncedSearch && (
              <ListItem>
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  No employees match.
                </Typography>
              </ListItem>
            )}

          {!loading &&
            !hasMore &&
            employeeList.length >
            0 && (
              <ListItem>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    mx: "auto",
                  }}
                >
                  All employees
                  loaded
                </Typography>
              </ListItem>
            )}
        </List>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          borderTop: 1,
          borderColor:
            "divider",
        }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
        >
          OK ({selectedIds.length}{" "}
          selected)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Day Selector Modal
// ------------------------------------------------------------------

function DaySelectorModal({
  open,
  onClose,
  selectedKeys,
  onSave,
}) {
  const [selected, setSelected] =
    useState(selectedKeys);

  useEffect(() => {
    if (open) {
      setSelected(
        selectedKeys
      );
    }
  }, [
    open,
    selectedKeys,
  ]);

  const toggleDay = (key) => {
    setSelected(
      (previous) =>
        previous.includes(key)
          ? previous.filter(
            (item) =>
              item !== key
          )
          : [
            ...previous,
            key,
          ]
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
        }}
      >
        Select Work Days
      </DialogTitle>

      <DialogContent
        dividers
        sx={{ p: 2 }}
      >
        <List dense>
          {DAYS.map(
            (day) => (
              <ListItem
                key={day.key}
                button
                onClick={() =>
                  toggleDay(
                    day.key
                  )
                }
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={selected.includes(
                      day.key
                    )}
                    disableRipple
                  />
                </ListItemIcon>

                <MuiListItemText
                  primary={
                    day.label
                  }
                />
              </ListItem>
            )
          )}
        </List>
      </DialogContent>

      <DialogActions
        sx={{ p: 2 }}
      >
        <Button
          variant="outlined"
          color="error"
          onClick={onClose}
        >
          Cancel
        </Button>

        <Button
          variant="contained"
          onClick={() => {
            onSave(selected);
            onClose();
          }}
        >
          OK ({selected.length}{" "}
          selected)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ------------------------------------------------------------------
// Main Roster Form
// ------------------------------------------------------------------

export default function RosterForm({
  onCreateRoster,
  onUpdateRoster,
}) {
  const dispatch =
    useDispatch();

  const {
    company_id,
    user_id,
    is_super,
    is_admin,
  } = useSelector(
    (state) =>
      state.GlobalVariables ||
      state.globalVariables ||
      {}
  );

  // --------------------------------------------------------------
  // Today / Financial Year
  // --------------------------------------------------------------

  const today = new Date();

  today.setHours(
    0,
    0,
    0,
    0
  );

  const { fyEnd } =
    getFYBounds(today);

  // --------------------------------------------------------------
  // Form state
  // --------------------------------------------------------------

  const [
    editingRosterId,
    setEditingRosterId,
  ] = useState(null);

  const [
    selectedEmployees,
    setSelectedEmployees,
  ] = useState([]);

  const [
    workingDays,
    setWorkingDays,
  ] = useState([]);

  const [
    startDate,
    setStartDate,
  ] = useState("");

  const [
    endDate,
    setEndDate,
  ] = useState("");

  const [
    shiftId,
    setShiftId,
  ] = useState(null);

  const [error, setError] =
    useState("");

  const [success, setSuccess] =
    useState(false);

  const [
    submitting,
    setSubmitting,
  ] = useState(false);

  // --------------------------------------------------------------
  // Shifts
  // --------------------------------------------------------------

  const [shifts, setShifts] =
    useState([]);

  const [
    shiftsLoading,
    setShiftsLoading,
  ] = useState(false);

  const [
    shiftsError,
    setShiftsError,
  ] = useState("");

  const [
    shiftsFetched,
    setShiftsFetched,
  ] = useState(false);

  // --------------------------------------------------------------
  // Modals
  // --------------------------------------------------------------

  const [
    employeeModalOpen,
    setEmployeeModalOpen,
  ] = useState(false);

  const [
    dayModalOpen,
    setDayModalOpen,
  ] = useState(false);

  // --------------------------------------------------------------
  // Date derived values
  // --------------------------------------------------------------

  const startDateObj =
    startDate
      ? new Date(
        `${startDate}T00:00:00`
      )
      : null;

  const maxEndByRange =
    startDateObj
      ? addMonths(
        startDateObj,
        3
      )
      : null;

  const maxEndDate =
    startDateObj
      ? maxEndByRange < fyEnd
        ? maxEndByRange
        : fyEnd
      : null;

  // --------------------------------------------------------------
  // Hide FAB
  // --------------------------------------------------------------

  useEffect(() => {
    dispatch(
      rSetShowFab(false)
    );

    return () => {
      dispatch(
        rSetShowFab(true)
      );
    };
  }, [dispatch]);

  // --------------------------------------------------------------
  // Fetch shifts
  // --------------------------------------------------------------

  const fetchShifts = async () => {
    if (!company_id) {
      return;
    }

    setShiftsLoading(true);
    setShiftsError("");

    try {
      const response =
        await configServ.getAllShifts(
          {
            company_id,
          }
        );

      if (
        response?.status === 200
      ) {
        const mapped =
          (
            response.data ||
            []
          ).map(
            (shift) => ({
              id: Number(
                shift.id
              ),

              name:
                shift.shift_name,

              code:
                shift.shift_code,

              start:
                shift.start_time,

              end:
                shift.end_time,

              timeRange:
                `${shift.start_time} - ${shift.end_time}`,
            })
          );

        setShifts(mapped);

        setShiftsFetched(
          true
        );
      } else {
        throw new Error(
          response?.message ||
          "Failed to fetch shifts"
        );
      }
    } catch (err) {
      const message =
        err?.response?.data
          ?.message ||
        err?.message ||
        "Could not load shifts";

      setShiftsError(
        message
      );

      // toast removed
    } finally {
      setShiftsLoading(false);
    }
  };

  // --------------------------------------------------------------
  // Edit event
  // --------------------------------------------------------------

  useEffect(() => {
    const handleRosterEdit =
      async (event) => {
        const roster =
          event.detail;

        if (!roster) {
          return;
        }

        setError("");
        setSuccess(false);

        setEditingRosterId(
          roster.roster_id ||
          roster.id ||
          null
        );

        // ----------------------------------------------------------
        // Dates
        // ----------------------------------------------------------

        setStartDate(
          toInputDate(
            roster.start_date ||
            roster.startDate
          )
        );

        setEndDate(
          toInputDate(
            roster.end_date ||
            roster.endDate
          )
        );

        // ----------------------------------------------------------
        // Shift
        // ----------------------------------------------------------

        setShiftId(
          roster.shift_id
            ? Number(
              roster.shift_id
            )
            : null
        );

        // ----------------------------------------------------------
        // Employee
        // ----------------------------------------------------------

        const employeeId =
          roster.employee_id;

        if (
          employeeId
        ) {
          setSelectedEmployees([
            {
              id: Number(
                employeeId
              ),

              name:
                roster.employee_name ||
                `Employee #${employeeId}`,

              code:
                roster.employee_code ||
                "",
            },
          ]);
        } else {
          setSelectedEmployees([]);
        }

        // ----------------------------------------------------------
        // Working days
        // ----------------------------------------------------------

        let editWorkingDays =
          [];

        if (
          Array.isArray(
            roster.workingDays
          )
        ) {
          editWorkingDays =
            roster.workingDays;
        } else if (
          Array.isArray(
            roster.day_of_week
          )
        ) {
          editWorkingDays =
            roster.day_of_week
              .map(
                (day) =>
                  DAY_NUMBER_TO_KEY[
                  Number(day)
                  ]
              )
              .filter(Boolean);
        } else if (
          Array.isArray(
            roster.days
          )
        ) {
          editWorkingDays =
            roster.days
              .map(
                (day) => {
                  if (
                    typeof day ===
                    "number"
                  ) {
                    return DAY_NUMBER_TO_KEY[
                      day
                    ];
                  }

                  return String(
                    day
                  )
                    .toLowerCase()
                    .substring(
                      0,
                      3
                    );
                }
              )
              .filter(Boolean);
        }

        setWorkingDays([
          ...new Set(
            editWorkingDays
          ),
        ]);

        // ----------------------------------------------------------
        // Load shifts
        // ----------------------------------------------------------

        if (
          !shiftsFetched
        ) {
          await fetchShifts();
        }

        window.scrollTo({
          top: 0,
          behavior:
            "smooth",
        });
      };

    window.addEventListener(
      "roster:edit",
      handleRosterEdit
    );

    return () => {
      window.removeEventListener(
        "roster:edit",
        handleRosterEdit
      );
    };

    // eslint-disable-next-line
  }, [shiftsFetched]);

  // --------------------------------------------------------------
  // Reset form
  // --------------------------------------------------------------

  const resetForm = () => {
    setEditingRosterId(
      null
    );

    setSelectedEmployees([]);

    setWorkingDays([]);

    setStartDate("");

    setEndDate("");

    setShiftId(null);

    setError("");

    setSuccess(false);
  };

  // --------------------------------------------------------------
  // Generate / Update
  // --------------------------------------------------------------

  const handleGenerate =
    async () => {
      setError("");
      setSuccess(false);

      // ------------------------------------------------------------
      // Validation
      // ------------------------------------------------------------

      if (
        workingDays.length ===
        0
      ) {
        return setError(
          "Select at least one working day."
        );
      }

      if (
        selectedEmployees.length ===
        0
      ) {
        return setError(
          "Select at least one employee."
        );
      }

      if (!shiftId) {
        return setError(
          "Select a shift timing."
        );
      }

      if (
        !startDate ||
        !endDate
      ) {
        return setError(
          "Please select both start and end dates."
        );
      }

      const start =
        new Date(
          `${startDate}T00:00:00`
        );

      const end =
        new Date(
          `${endDate}T00:00:00`
        );

      // ------------------------------------------------------------
      // Back date
      // ------------------------------------------------------------

      if (
        start < today
      ) {
        return setError(
          "Start date can't be a back date — pick today or a future date."
        );
      }

      // ------------------------------------------------------------
      // End date
      // ------------------------------------------------------------

      if (end < start) {
        return setError(
          "End date can't be before the start date."
        );
      }

      // ------------------------------------------------------------
      // Financial year
      // ------------------------------------------------------------

      if (
        start > fyEnd ||
        end > fyEnd
      ) {
        return setError(
          "Dates must fall within the current financial year (Apr–Mar)."
        );
      }

      // ------------------------------------------------------------
      // Maximum 3 months
      // ------------------------------------------------------------

      if (
        end >
        addMonths(
          start,
          3
        )
      ) {
        return setError(
          "A roster can span a maximum of 3 months."
        );
      }

      try {
        setSubmitting(true);

        // ----------------------------------------------------------
        // UPDATE
        // ----------------------------------------------------------

        if (
          editingRosterId
        ) {
          /*
           * IMPORTANT:
           * Employee is NOT changed during update.
           *
           * Therefore employeeIds is intentionally
           * NOT sent in update request.
           */

          const updatePayload =
          {
            id: Number(
              editingRosterId
            ),

            workingDays,

            startDate,

            endDate,

            shift_id:
              Number(
                shiftId
              ),
          };

          if (
            onUpdateRoster
          ) {
            await onUpdateRoster(
              editingRosterId,
              updatePayload
            );
          } else {
            await configServ.updateRoster(
              updatePayload
            );
          }
        }

        // ----------------------------------------------------------
        // CREATE
        // ----------------------------------------------------------

        else {
          /*
           * Employee is required while creating.
           */

          const createPayload =
          {
            workingDays,

            employeeIds:
              selectedEmployees.map(
                (employee) =>
                  Number(
                    employee.id
                  )
              ),

            startDate,

            endDate,

            shift_id:
              Number(
                shiftId
              ),
          };

          if (
            onCreateRoster
          ) {
            await onCreateRoster(
              createPayload
            );
          } else {
            await configServ.createRoster(
              createPayload
            );
          }
        }

        // ----------------------------------------------------------
        // Success
        // ----------------------------------------------------------

        // toast removed

        setSuccess(true);

        // ----------------------------------------------------------
        // Refresh roster list
        // ----------------------------------------------------------

        window.dispatchEvent(
          new CustomEvent(
            "roster:refresh"
          )
        );

        // ----------------------------------------------------------
        // Reset
        // ----------------------------------------------------------

        resetForm();

      } catch (err) {

        const message =
          err?.response?.data
            ?.message ||
          err?.message ||
          "Failed to save roster";

        setError(message);

        // toast removed

      } finally {

        setSubmitting(
          false
        );
      }
    };

  // --------------------------------------------------------------
  // Employee display
  // --------------------------------------------------------------

  const getEmployeeDisplay =
    () => {
      if (
        selectedEmployees.length ===
        0
      ) {
        return "Select employees";
      }

      if (
        selectedEmployees.length <=
        2
      ) {
        return selectedEmployees
          .map(
            (employee) =>
              employee.code ||
              employee.name
          )
          .join(", ");
      }

      return `${selectedEmployees.length} selected`;
    };

  // --------------------------------------------------------------
  // Day display
  // --------------------------------------------------------------

  const getDayDisplay =
    () => {
      if (
        workingDays.length ===
        0
      ) {
        return "Select days";
      }

      return workingDays
        .map(
          (key) =>
            DAYS.find(
              (day) =>
                day.key ===
                key
            )?.short
        )
        .filter(Boolean)
        .join(", ");
    };

  // --------------------------------------------------------------
  // Shift options
  // --------------------------------------------------------------

  const renderShiftOptions =
    () => {
      if (shiftsLoading) {
        return (
          <MenuItem disabled>
            <CircularProgress
              size={24}
              sx={{ mr: 1 }}
            />

            Loading shifts...
          </MenuItem>
        );
      }

      if (shiftsError) {
        return (
          <MenuItem disabled>
            Error:{" "}
            {shiftsError}
          </MenuItem>
        );
      }

      if (
        shifts.length ===
        0 &&
        shiftsFetched
      ) {
        return (
          <MenuItem disabled>
            No shifts found
          </MenuItem>
        );
      }

      if (
        !shiftsFetched &&
        !shiftsLoading
      ) {
        return (
          <MenuItem disabled>
            Click to load shifts
          </MenuItem>
        );
      }

      return shifts.map(
        (shift) => (
          <MenuItem
            key={shift.id}
            value={shift.id}
          >
            <MuiListItemText
              primary={
                shift.name
              }
              secondary={
                shift.code
              }
            />
          </MenuItem>
        )
      );
    };

  // --------------------------------------------------------------
  // UI
  // --------------------------------------------------------------

  return (
    <Box
      sx={{
        p: 2,
        width: "100%",
        minHeight:
          "100vh",
        bgcolor:
          "#ffffff",
      }}
    >
      {/* Heading */}

      <Typography
        variant="h5"
        align="center"
        fontWeight="bold"
        color="primary"
        sx={{
          mb: 3,
          mt: 2,
        }}
      >
        {editingRosterId
          ? "EDIT EMPLOYEE ROSTER"
          : "ROSTER PLANNING"}
      </Typography>

      {/* Form */}

      <Card
        sx={{
          p: 3,
          mb: 3,
          borderRadius:
            "12px",
          borderLeft:
            "2px solid #1976d2",
          boxShadow: 2,
        }}
      >
        <Grid
          container
          spacing={3}
        >
          {/* Employee */}

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
          >
            <FormControl
              fullWidth
            >
              <InputLabel id="employee-label">
                Employee
              </InputLabel>

              <Select
                labelId="employee-label"
                multiple
                value={selectedEmployees.map(
                  (employee) =>
                    Number(
                      employee.id
                    )
                )}
                open={false}
                onOpen={() =>
                  setEmployeeModalOpen(
                    true
                  )
                }
                input={
                  <OutlinedInput
                    label="Employee"
                    startAdornment={
                      <GroupsIcon
                        sx={{
                          mr: 1,
                          color:
                            "text.disabled",
                        }}
                        fontSize="small"
                      />
                    }
                  />
                }
                renderValue={
                  getEmployeeDisplay
                }
              />
            </FormControl>
          </Grid>

          {/* Working Days */}

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
          >
            <FormControl
              fullWidth
            >
              <InputLabel id="working-days-label">
                Assign Work Days
              </InputLabel>

              <Select
                labelId="working-days-label"
                multiple
                value={
                  workingDays
                }
                open={false}
                onOpen={() =>
                  setDayModalOpen(
                    true
                  )
                }
                input={
                  <OutlinedInput
                    label="Assign Work Days"
                    startAdornment={
                      <ChecklistIcon
                        sx={{
                          mr: 1,
                          color:
                            "text.disabled",
                        }}
                        fontSize="small"
                      />
                    }
                  />
                }
                renderValue={
                  getDayDisplay
                }
              />
            </FormControl>
          </Grid>

          {/* Start Date */}

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
          >
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={
                startDate
              }
              helperText={`From ${fmtDMY(
                today
              )}`}
              InputLabelProps={{
                shrink:
                  true,
              }}
              inputProps={{
                min:
                  toInputDate(
                    today
                  ),

                max:
                  toInputDate(
                    fyEnd
                  ),
              }}
              onChange={(event) => {
                const value =
                  event.target
                    .value;

                setStartDate(
                  value
                );

                if (!value) {
                  setEndDate("");
                  return;
                }

                if (
                  endDate &&
                  new Date(
                    `${endDate}T00:00:00`
                  ) <
                  new Date(
                    `${value}T00:00:00`
                  )
                ) {
                  setEndDate("");
                }
              }}
            />
          </Grid>

          {/* End Date */}

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
          >
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={
                endDate
              }
              helperText="Maximum span: 3 months"
              disabled={
                !startDate
              }
              InputLabelProps={{
                shrink:
                  true,
              }}
              inputProps={{
                min:
                  startDate ||
                  toInputDate(
                    today
                  ),

                max:
                  maxEndDate
                    ? toInputDate(
                      maxEndDate
                    )
                    : toInputDate(
                      fyEnd
                    ),
              }}
              onChange={(event) =>
                setEndDate(
                  event.target
                    .value
                )
              }
            />
          </Grid>

          {/* Shift */}

          <Grid
            item
            xs={12}
            sm={6}
            md={4}
          >
            <FormControl
              fullWidth
            >
              <InputLabel id="shift-label">
                Shift Timing
              </InputLabel>

              <Select
                labelId="shift-label"
                value={
                  shiftId ?? ""
                }
                label="Shift Timing"
                onChange={(event) =>
                  setShiftId(
                    Number(
                      event.target
                        .value
                    )
                  )
                }
                onOpen={() => {
                  if (
                    !shiftsFetched &&
                    !shiftsLoading
                  ) {
                    fetchShifts();
                  }
                }}
                input={
                  <OutlinedInput
                    label="Shift Timing"
                    startAdornment={
                      <AccessTimeIcon
                        sx={{
                          mr: 1,
                          color:
                            "text.disabled",
                        }}
                        fontSize="small"
                      />
                    }
                  />
                }
                renderValue={(
                  value
                ) => {
                  const shift =
                    shifts.find(
                      (item) =>
                        Number(
                          item.id
                        ) ===
                        Number(
                          value
                        )
                    );

                  return shift
                    ? `${shift.name} · ${shift.code}`
                    : "Select shift";
                }}
                disabled={
                  shiftsLoading
                }
              >
                {renderShiftOptions()}
              </Select>
            </FormControl>
          </Grid>

          {/* Alerts */}

          <Grid item xs={12}>
            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2 }}
              >
                {error}
              </Alert>
            )}

            {success &&
              !error && (
                <Alert
                  severity="success"
                  sx={{ mt: 2 }}
                >
                  Roster saved
                  successfully.
                </Alert>
              )}
          </Grid>
        </Grid>

        <Divider
          sx={{ my: 3 }}
        />

        {/* Actions */}

        <Box
          sx={{
            display:
              "flex",
            alignItems:
              "center",
            justifyContent:
              "space-between",
            flexWrap:
              "wrap",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display:
                "flex",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              size="large"
              disabled={
                submitting ||
                shiftsLoading
              }
              onClick={
                handleGenerate
              }
              endIcon={
                editingRosterId ? (
                  <EditIcon />
                ) : (
                  <ArrowForwardIcon />
                )
              }
              sx={{
                bgcolor:
                  "#242B52",

                "&:hover": {
                  bgcolor:
                    "#1A2043",
                },

                borderRadius: 2,
                px: 3,
                textTransform:
                  "none",
                fontWeight: 600,
              }}
            >
              {submitting
                ? editingRosterId
                  ? "Updating…"
                  : "Generating…"
                : editingRosterId
                  ? "Update Roster"
                  : "Generate Roster"}
            </Button>

            {editingRosterId && (
              <Button
                variant="outlined"
                size="large"
                color="error"
                startIcon={
                  <CloseIcon />
                }
                onClick={
                  resetForm
                }
                disabled={
                  submitting
                }
                sx={{
                  borderRadius: 2,
                  textTransform:
                    "none",
                }}
              >
                Cancel Edit
              </Button>
            )}
          </Box>
        </Box>
      </Card>

      {/* Employee Modal */}

      <EmployeeSelectorModal
        open={
          employeeModalOpen
        }
        onClose={() =>
          setEmployeeModalOpen(
            false
          )
        }
        selectedEmployees={
          selectedEmployees
        }
        onSave={(
          employees
        ) =>
          setSelectedEmployees(
            employees
          )
        }
        companyId={
          company_id
        }
        userId={
          user_id
        }
        isAdmin={Boolean(
          is_super ||
          is_admin
        )}
      />

      {/* Day Modal */}

      <DaySelectorModal
        open={
          dayModalOpen
        }
        onClose={() =>
          setDayModalOpen(
            false
          )
        }
        selectedKeys={
          workingDays
        }
        onSave={(keys) =>
          setWorkingDays(
            keys
          )
        }
      />
    </Box>
  );
}